import { HOME_ROUTES, LOGIN_ROUTES, MFA_SESSION_KEYS, TOKENS } from 'pages/auth/constants';
import { SignInResponse } from '../types';
import { NavigateFunction } from 'react-router-dom';
import { APIS, isRenderedAsIframeInNewUI } from 'config';
import axiosHttp from 'axios';
import { SITE_TYPE } from 'pages/settings/sections/sites/types';
import { postIframeMessage } from 'utils/postPerentIframeMessage';
import { SESSION_LOGGING_MESSAGE_KEY } from 'pages/SiteLoadingPage/constants';

const BASE_URL = APIS.AUTHN;

export const DEFAULT_USER_IDLE_TIME = 20 * 60 * 1000;

export enum SignInResponseCode {
  Authenticated = 1,
  ValidatePhone = 2,
  ValidateEmail = 3,
  MfaPinCode = 4,
  PasswordExpired = 5,
  WrongPassword = 6,
  Locked = 7,
  InvalidSubdomain = 8,
  SsoRedirect = 9
}

export const loginHandler = async (
  {
    code,
    accessExpirationInMS,
    sessionId,
    email,
    data,
    targetUrl
  }: SignInResponse & { email?: string; sessionId?: string },
  navigate: NavigateFunction
) => {
  switch (code) {
    case SignInResponseCode.Authenticated:
      await navigateToApplication({ accessExpirationInMS, sessionId }, navigate);
      break;
    case SignInResponseCode.ValidatePhone:
      routeToWithData(
        LOGIN_ROUTES.PHONE_VERIFICATION,
        {
          fromSignIn: true,
          resendContact: true,
          contactType: code,
          message: '',
          phone: data
        },
        navigate
      );
      break;
    case SignInResponseCode.ValidateEmail:
      routeToWithData(
        LOGIN_ROUTES.PHONE_VERIFICATION,
        {
          fromSignIn: true,
          resendContact: true,
          contactType: code,
          message: '',
          email
        },
        navigate
      );

      break;
    case SignInResponseCode.MfaPinCode:
      routeToWithData(
        LOGIN_ROUTES.QR_VERIFICATION,
        {
          is_required: true,
          message: '',
          shouldShowQr: false,
          mfa_qr_code_base64: '',
          mfa_manual_entry_key: ''
        },
        navigate
      );

      break;

    case SignInResponseCode.PasswordExpired:
      navigate(LOGIN_ROUTES.PASSWORD_EXPIRED);
      break;

    case SignInResponseCode.SsoRedirect:
      if (data) {
        // use window native navigate as SSORedirectUrl is on an another domain
        window.location.href = data;
      }
      break;

    case SignInResponseCode.Locked:
      window.location.replace(targetUrl || 'https://www.cynet.com/unlock-your-account');
      return;

    default:
      break;
  }
};

export const sendQrPinCodeHandler = async (navigate: NavigateFunction, response: SignInResponse) => {
  await navigateToApplication(response, navigate);
};

export const sendPinCodeHandler = ({ key, QR }: SignInResponse, navigate: NavigateFunction) => {
  routeToWithData(
    LOGIN_ROUTES.QR_VERIFICATION,
    {
      is_required: true,
      message: '',
      shouldShowQr: true,
      mfa_qr_code_base64: QR?.replace('data:image/png;base64,', ''),
      mfa_manual_entry_key: key
    },
    navigate
  );
};

const routeToWithData = (routeName: string, state: object, navigate: NavigateFunction) => {
  navigate(routeName, {
    state: state
  });
};

export const logoutMicroservices = async (reasonCode: number) => {
  const sessionId = sessionStorage.getItem('sessionId');
  const url = `${BASE_URL}/auth/signout?l=${reasonCode || ''}${sessionId ? `&sid=${sessionId}` : ''}`;
  sessionStorage.removeItem('sessionId');
  await axiosHttp.get(url, { withCredentials: true });
};

export const sendRefreshTokenRequest = () => {
  const sessionId = sessionStorage.getItem('sessionId');
  const url = `${BASE_URL}/auth/refresh${sessionId ? `?sid=${sessionId}` : ''}`;
  return axiosHttp.get(url);
};

const setRefreshToken = (addedMilliseconds: number) => {
  const newExpirationTime = new Date(new Date().getTime() + addedMilliseconds).getTime();
  localStorage.setItem(TOKENS.ACCESS_TOKEN_EXPIRATION, JSON.stringify(newExpirationTime));
};

const setAuthToken = () => {
  localStorage.setItem(TOKENS.AUTH_TOKEN_SESSION_KEY, JSON.stringify(new Date().getTime().toString()));
};

const setIdleTimeoutValue = (ms: number) => {
  localStorage.setItem(TOKENS.IDLE_TIMEOUT_TOKEN, JSON.stringify(ms));
};

const getNavigationUrlBySiteType = (siteType: SITE_TYPE) => {
  switch (siteType) {
    case SITE_TYPE.SiteManager:
    case SITE_TYPE.Account:
      return HOME_ROUTES.global;
    case SITE_TYPE.Site:
      return HOME_ROUTES.local;
    default:
      throw new Error('error in session initializing process');
  }
};

export const initLogin = async (http = axiosHttp, userGuid?: string) => {
  const url = `/WebApp/SessionInitializer/SessionInitializer`;
  if (isRenderedAsIframeInNewUI) {
    postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
      step: 'SessionInitializer:start',
      details: { userGuid }
    });
  }
  try {
    const res = await http.get(url, { withCredentials: true, params: { u: userGuid } });
    const { SiteType: siteType } = res?.data;
    if (isRenderedAsIframeInNewUI) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'SessionInitializer:success',
        details: { userGuid, siteType }
      });
    }
    return { siteType };
  } catch (error) {
    if (isRenderedAsIframeInNewUI) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        type: 'error',
        step: 'SessionInitializer:error',
        details: {
          error: JSON.stringify(error),
          userGuid,
          message: 'SessionInitializer request failed'
        }
      });
    }
    throw error;
  }
};

export async function navigateToApplication(
  data: { accessExpirationInMS: number; targetUrl?: string; refreshPage?: boolean; sessionId?: string },
  navigate: NavigateFunction,
  http = axiosHttp
) {
  if (data.targetUrl) {
    window.location.replace(data.targetUrl);
    return;
  }
  if (data.sessionId) {
    sessionStorage.setItem('sessionId', data.sessionId);
  }
  const { siteType } = await initLogin(http);

  if (window.setLoginAccessExpirationInMS && !isNaN(window.setLoginAccessExpirationInMS)) {
    data.accessExpirationInMS = window.setLoginAccessExpirationInMS;
    // eslint-disable-next-line no-console
    console.log(
      `navigateToApplication() - data.accessExpirationInMS was manually set to: ${(
        data.accessExpirationInMS / 1000
      ).toFixed(2)} seconds (${(data.accessExpirationInMS / 1000 / 60).toFixed(2)} minutes)`
    );
  }

  // client-side guard
  setAuthToken();
  setRefreshToken(data.accessExpirationInMS);
  setIdleTimeoutValue(data.accessExpirationInMS);

  const navigateTo = getNavigationUrlBySiteType(siteType);
  if (data.refreshPage) {
    window.location.replace(window.location.origin + navigateTo);
  }
  navigate(navigateTo);
}

export const getMfaState = (locationState: unknown, sessionKey: keyof typeof MFA_SESSION_KEYS) => {
  if (locationState) {
    return locationState;
  }
  if (typeof sessionStorage.getItem(MFA_SESSION_KEYS[sessionKey]) === 'string') {
    return JSON.parse(sessionStorage.getItem(MFA_SESSION_KEYS[sessionKey]) as string);
  }
  return {};
};
